// Everything the Activity & Collaboration Trend tab decides that is not React.
//
// OKO2-3038. The tab groups THE ROWS THE PAGE ALREADY HOLDS into periods. It
// does not ask a second endpoint and it does not re-measure anything: a
// period's session count, its minutes and its average level are the Session
// Detail rows' own values, added up. That is the same choice the CSV made
// (D48.21), for the same reason — the chart, the table and the strip above
// them are then one measurement grouped three ways, and cannot disagree.
//
// THE ONE RULE FROM THE DETAIL TAB CARRIES OVER: MISSING IS NEVER ZERO. A
// period with no minutes recorded reads as an em dash, not "0 Min"; a session
// with no collaboration level is counted but does not drag an average down.

import { constants } from "utils";

import { EM_DASH } from "./studentReportTable";

// The four collaboration levels, as the design's Key names and colours them.
// Ordered as the Key draws them: Lv1 first, the same order the stacks rise in.
export const COLLAB_LEVELS = [
  { level: 1, fill: "#FE1E7E", short: "Lv1", name: "Needs Intervention" },
  { level: 2, fill: "#EAB308", short: "Lv2", name: "Needs Guidance" },
  { level: 3, fill: "#00A35A", short: "Lv3", name: "Effective Collaborator" },
  { level: 4, fill: "#605DEC", short: "Lv4", name: "Exceeding Expectations" },
];

// A session that ran but was never scored. The chart no longer draws these
// (its Key has the design's four levels and nothing else); the fill stays as
// the fallback for a level outside those four.
export const UNSCORED_FILL = "#E7E7E7";

const LEVEL_FILLS = COLLAB_LEVELS.reduce(
  (all, entry) => ({ ...all, [entry.level]: entry.fill }),
  {}
);

/** The colour one session's block is drawn in. */
export const levelFill = (level) => LEVEL_FILLS[level] || UNSCORED_FILL;

const DAY_MS = 24 * 60 * 60 * 1000;

export const GROUPING = { WEEK: "week", MONTH: "month" };

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const parseDate = (value) => {
  // `new Date(null)` is the epoch, not an invalid date, so an absent
  // `started_at` would otherwise file the session under 1970 and stretch the
  // chart across fifty-odd years of empty periods.
  if (value === null || value === undefined || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * The Monday 00:00 that starts the week containing `value`, in the BROWSER's
 * timezone.
 *
 * MONDAY, and not the Sunday the mock's example dates happen to fall on: every
 * other weekly surface in the product is Monday-based — the collaboration
 * heatmap, the dosage chart and the Postgres `date_trunc('week', ...)` behind
 * them all are — and a student page that split weeks one day earlier would put
 * the same session in a different week from the class page a teacher opens
 * next to it. The design's FORMAT is reproduced exactly; its boundary is not.
 *
 * Local, not UTC, because the row above it is dated locally too
 * (`sessionDateParts`). A session shown as 7/13 must land in the period that
 * contains 7/13 on this same screen, whatever the viewer's offset.
 */
export const weekStart = (value) => {
  const date = parseDate(value);
  if (!date) return null;
  const local = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  // getDay() is 0 on Sunday, which is six days INTO its Monday-based week.
  local.setDate(local.getDate() - ((local.getDay() + 6) % 7));
  return local;
};

/** The 1st of the month containing `value`, in the browser's timezone. */
export const monthStart = (value) => {
  const date = parseDate(value);
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

const periodStart = (value, grouping) =>
  grouping === GROUPING.MONTH ? monthStart(value) : weekStart(value);

/** A stable key for a period: the ISO date of its first day, in local terms. */
export const periodKey = (start) => {
  if (!start) return null;
  const month = String(start.getMonth() + 1).padStart(2, "0");
  const day = String(start.getDate()).padStart(2, "0");
  return `${start.getFullYear()}-${month}-${day}`;
};

const shortDate = (date) =>
  `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;

/**
 * How a period is spelled. A week is its two ends; a month is `Jan ‘27`, with
 * the design's own curly apostrophe.
 *
 * A week returns two parts because the chart breaks it over two lines and the
 * table writes it on one. A month is short enough to need only the one.
 */
export const periodLabelParts = (start, grouping) => {
  if (grouping === GROUPING.MONTH) {
    const year = String(start.getFullYear()).slice(-2);
    return { start: `${MONTH_NAMES[start.getMonth()]} ‘${year}`, end: null };
  }
  const end = new Date(start.getTime() + 6 * DAY_MS);
  return { start: shortDate(start), end: shortDate(end) };
};

/** One line: "6/29/2026 - 7/5/2026" (weeks run Monday to Sunday), or "Jan ‘27". */
export const periodLabel = (start, grouping) => {
  const { start: head, end } = periodLabelParts(start, grouping);
  return end ? `${head} - ${end}` : head;
};

/**
 * The Session Reports link for one period: this student, this class when one is
 * chosen, and exactly the Monday-to-Sunday window the row's label states.
 *
 * Not `sessionReportUrl`, which always sets `date_range=all_time`: Session
 * History re-applies `date_range` after an explicit window, so a link carrying
 * both would not land on the window.
 */
export const periodReportUrl = ({ start, studentId, classroomId } = {}) => {
  if (!start) return null;
  const params = new URLSearchParams();
  if (studentId) params.set("student_id", studentId);
  if (classroomId) params.set("classroom_id", classroomId);
  params.set("start_date", periodKey(start));
  params.set("end_date", periodKey(new Date(start.getTime() + 6 * DAY_MS)));
  // Session Reports opens By Lesson by default; this link is about sessions.
  params.set("tab", "groups");
  return `${constants.SESSION_HISTORY_PATH}?${params.toString()}`;
};

/** The name the first column and the CSV give this period. */
export const periodNoun = (grouping) =>
  grouping === GROUPING.MONTH ? "Month" : "Week";

/** The chart's own title, which names the grouping the way the design does. */
export const chartTitle = (grouping) =>
  `Collaboration Trend By ${periodNoun(grouping)}`;

/**
 * Minutes as this table spells them: "66 Min".
 *
 * NOT `minutesText`, which spells the same number "66 MIN" for a KPI tile that
 * uppercases it again in CSS. A measured 0 keeps its zero; nothing measured
 * does not become one.
 */
export const weekMinutesText = (value) =>
  value === null || value === undefined ? EM_DASH : `${Math.round(value)} Min`;

/**
 * A period's average collaboration level, over the sessions that HAVE one.
 *
 * An unscored session is not a zero, and counting it as one would push a whole
 * period a level down.
 */
export const averageCollabLevel = (levels) => {
  const scored = (levels || []).filter(
    (level) => level !== null && level !== undefined
  );
  if (!scored.length) return null;
  return scored.reduce((sum, level) => sum + level, 0) / scored.length;
};

/**
 * The level tag a period's average is drawn as.
 *
 * Rounded HALF UP, which is what the design's own numbers do: a week of Level 3
 * and Level 2 averages 2.5 and is drawn "Level 3".
 */
export const roundedLevel = (average) =>
  average === null || average === undefined ? null : Math.round(average);

const advance = (cursor, grouping) => {
  if (grouping === GROUPING.MONTH) cursor.setMonth(cursor.getMonth() + 1);
  else cursor.setDate(cursor.getDate() + 7);
};

/**
 * Group the rows into periods, oldest first, WITH THE EMPTY ONES BETWEEN THEM.
 *
 * A period the child ran nothing in is a fact about a trend, and a chart that
 * closes the gap draws scattered periods as though they were consecutive —
 * which is the one thing a trend must not do. The span runs from the first
 * period with a session to the last and is not padded at either end, so an
 * empty period only ever appears BETWEEN two real ones.
 *
 * Sessions inside a period are ordered by level ASCENDING, because the chart
 * stacks them from the baseline up and the design's stacks read L1 at the
 * bottom through L4 at the top. Unscored sessions sit above the scored ones.
 */
export const buildPeriods = (rows = []) => {
  const dated = (rows || [])
    .map((row) => ({ row, at: parseDate(row?.started_at) }))
    .filter((entry) => entry.at);

  if (!dated.length) return { grouping: GROUPING.WEEK, periods: [] };

  // By week, whatever the span: the Dosage & Collaboration frame (2639:18134)
  // draws the chart and the table week by week. The earlier switch to months
  // past nine weeks (section 2879:28889) is withdrawn.
  const grouping = GROUPING.WEEK;

  const byPeriod = new Map();
  dated.forEach(({ row, at }) => {
    const start = periodStart(at, grouping);
    const key = periodKey(start);
    if (!byPeriod.has(key)) byPeriod.set(key, { key, start, sessions: [] });
    byPeriod.get(key).sessions.push({
      session_id: row.session_id,
      level: row.student_collab_level ?? null,
      minutes: row.duration_minutes ?? null,
    });
  });

  const found = [...byPeriod.values()].sort((a, b) => a.start - b.start);
  const spanned = [];
  const last = found[found.length - 1].start;
  for (let cursor = new Date(found[0].start); cursor <= last; advance(cursor, grouping)) {
    const start = new Date(cursor);
    const key = periodKey(start);
    spanned.push(byPeriod.get(key) || { key, start, sessions: [] });
  }

  return {
    grouping,
    periods: spanned.map((period) => {
      const sessions = [...period.sessions].sort(
        (a, b) => (a.level ?? Infinity) - (b.level ?? Infinity)
      );
      const measured = sessions
        .map((session) => session.minutes)
        .filter((value) => value !== null && value !== undefined);
      const average = averageCollabLevel(sessions.map((session) => session.level));

      return {
        key: period.key,
        start: period.start,
        label: periodLabel(period.start, grouping),
        labelParts: periodLabelParts(period.start, grouping),
        sessions,
        sessionCount: sessions.length,
        // Null rather than 0 when NOTHING was measured, so an empty period and
        // one whose minutes failed to resolve do not both read "0 Min".
        totalMinutes: measured.length
          ? measured.reduce((sum, value) => sum + value, 0)
          : null,
        averageCollabLevel: average,
        level: roundedLevel(average),
      };
    }),
  };
};

// The y axis carries AT MOST SIX LABELS. Drawing one per session is what the
// week chart does only because a busy week is three; a busy month is twenty,
// and twenty-one gridlines is a grey hatch rather than a scale. The design
// answers this by counting months in fives (0, 5, 10, 15, 20).
const NICE_STEPS = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000];
const MAX_TICKS = 5;

/** The step between axis labels: the smallest nice one that fits the ticks. */
export const tickStep = (max) =>
  NICE_STEPS.find((step) => Math.ceil(max / step) <= MAX_TICKS) ||
  NICE_STEPS[NICE_STEPS.length - 1];

// The design's scale: three sessions fill the plot at 65px each. A quieter
// student keeps that scale rather than having one session stretched tall.
const MIN_AXIS_TOP = 3;

/**
 * The value the axis tops out at: the busiest period, raised to the next label,
 * and never below the design's three.
 *
 * Raised rather than truncated, so the tallest bar always ends ON a gridline
 * and never overflows past the top of the plot.
 */
export const axisTop = (periods = []) => {
  const busiest = Math.max(1, ...periods.map((period) => period.sessionCount));
  const step = tickStep(busiest);
  return Math.max(MIN_AXIS_TOP, Math.ceil(busiest / step) * step);
};

/** Every labelled value on the axis, 0 first. */
export const axisTicks = (top) => {
  const step = tickStep(top);
  const ticks = [];
  for (let value = 0; value <= top; value += step) ticks.push(value);
  return ticks;
};

// ---- the sort ------------------------------------------------------------
//
// All four columns sort, as the design's four sort controls say they do. Same
// rules as the Detail table: unknowns group at one end, and the third click
// returns the periods to chronological order.

export const PERIOD_SORT_KEYS = {
  PERIOD: "period",
  SESSION_COUNT: "session_count",
  TOTAL_TIME: "total_time",
  AVERAGE_LEVEL: "average_level",
};

const periodSortValue = (period, column) => {
  switch (column) {
    case PERIOD_SORT_KEYS.PERIOD:
      return period.start.getTime();
    case PERIOD_SORT_KEYS.SESSION_COUNT:
      return period.sessionCount;
    case PERIOD_SORT_KEYS.TOTAL_TIME:
      return period.totalMinutes;
    case PERIOD_SORT_KEYS.AVERAGE_LEVEL:
      // The UNROUNDED average orders the rows: two periods both drawn "Level 3"
      // are 2.5 and 3.4, and sorting on the tag would call them equal.
      return period.averageCollabLevel;
    default:
      return null;
  }
};

const isUnknown = (value) => value === null || value === undefined || value === "";

/** Reorder the periods for display. Never mutates, never drops. */
export const sortPeriods = (periods, sort) => {
  if (!sort?.column) return [...(periods || [])];

  const factor = sort.direction === "desc" ? -1 : 1;
  return [...periods]
    .map((period, index) => ({ period, index }))
    .sort((a, b) => {
      const left = periodSortValue(a.period, sort.column);
      const right = periodSortValue(b.period, sort.column);
      if (isUnknown(left) && isUnknown(right)) return a.index - b.index;
      if (isUnknown(left)) return 1;
      if (isUnknown(right)) return -1;
      if (left < right) return -1 * factor;
      if (left > right) return 1 * factor;
      return a.index - b.index;
    })
    .map(({ period }) => period);
};

/**
 * The weeks in the order the table shows them: grouped from the rows, then
 * sorted as the teacher asked. The table and both downloads read this one
 * order.
 */
export const displayedPeriods = (rows, sort) => sortPeriods(buildPeriods(rows).periods, sort);

// ---- the CSV -------------------------------------------------------------

const csvCell = (value) => {
  const text = value === null || value === undefined ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const csvLine = (cells) => cells.map(csvCell).join(",");

// An unknown is one consistent EMPTY cell, as the Detail tab's file already
// spells it: an em dash is what a reader wants and what a spreadsheet cannot
// average.
const csvValue = (text) => (text === EM_DASH ? "" : text);

export const periodCsvHeaders = (grouping) => [
  periodNoun(grouping),
  "Session Count",
  "Total Session Time",
  "Average Collaboration Score",
];

/**
 * The file: the four columns the table shows, in the order it shows them. The
 * periods arrive already sorted and are written as they come.
 */
export const buildPeriodsCsv = ({ periods = [], grouping = GROUPING.WEEK } = {}) =>
  `${[
    csvLine(periodCsvHeaders(grouping)),
    ...periods.map((period) =>
      csvLine([
        period.label,
        period.sessionCount,
        csvValue(weekMinutesText(period.totalMinutes)),
        period.level === null ? "" : `Level ${period.level}`,
      ])
    ),
  ].join("\n")}\n`;
